import { display_classes, display_colors } from 'atomizify/doc'
import { useState, useEffect } from 'react'
import { Div, Span, Component } from './lib.js'
import { developer_types } from './data.js'

const App = () => {
  const [css_sheet, set_css_sheet] = useState(null)
  const [css_colors, set_css_colors] = useState(null)

  useEffect(() => {
    if (!css_sheet) return
    display_classes({ node: css_sheet, open: true, flattened: false })
  }, [css_sheet])

  useEffect(() => {
    if (!css_colors) return
    display_colors({ node: css_colors, open: true })
  }, [css_colors])

  return (
    <Page>
      <Title>Atomizify</Title>
      <Div w100p fs50 sans_serif grey6>
        <Div mb90>
          Atomizify is a small JavaScript library which generates a bunch of
          atomic CSS classes.
        </Div>
        <Div mb100>
          <Section>How to get it ?</Section>
          <Text>Install the module in your folder:</Text>
          <Code>{`npm i atomizify`}</Code>
          <Text>Import it in your file:</Text>
          <Code>{highlight(`import { *$atomizify* } from 'atomizify'`)}</Code>
        </Div>
        <Div mb100>
          <Section>How to use it ?</Section>
          <Div mt50 mb40>
            Atomizify can fit all types of developers, from lazy to geekiest
            ones!
            <br />
            It provides a few options to play with it: set some filters, choose
            your typecase system, or add your own custom classes.
            <br />
            <Div mt50>Choose the profile that suits you the best:</Div>
          </Div>
          <Div w100p mt80>
            {developer_types.map((type) => (
              <Type key={type.type} {...type} />
            ))}
          </Div>
          <Div mt100>
            Atomizify also works with React: it allows you to convert all the
            generated atomic classes in elegant-as-hell flags, that you can
            comfortably use & combine to build modular components.
          </Div>
        </Div>
        <Div mt120>
          <Section>How to document it ?</Section>
          <Div mt50 mb70>
            Atomizify also provides some ready-to-use generated documentation,
            so you can see at a glance all the magnificent CSS classes that have
            been generated.
            <br />
            <Div mt50>Import & call the desired function(s) in your file:</Div>
          </Div>
          <Text>
            If you'd like to display the CSS code generated, be my guest:
          </Text>
          <Code>{highlight(test2)}</Code>
          <Div mt60 mb100 elemRef={set_css_sheet} />
          <Text>
            If you'd like to display a chart of the generated colors, suit
            yourself:
          </Text>
          <Code>{highlight(test)}</Code>
          <Div mt60 elemRef={set_css_colors} />
        </Div>
      </Div>
    </Page>
  )
}

const test = `import { *$display_colors* } from 'atomizify/doc'

*/// the 'display_colors' function can be called without argument
// it also accepts the following options, passed in an object: 'node', 'open', 'circles'*
*$display_colors*({ 
  *%node*: my_html_node, */// you can pass a HTML node that will contain the chart ; default is 'document.body'*
  *%open*: false, */// you can set the initial state of the component displaying the content ; default is 'true'*
  *%circles*: false, */// you can set the visual shape of the colors as circles ; default is 'false'*
})`

const test2 = `import { *$display_classes* } from 'atomizify/doc'

*/// the 'display_classes' function can be called without argument
// it also accepts the following options, passed in an object: 'node', 'open', 'flattened'*
*$display_classes*({ 
  *%node*: my_html_node, */// you can pass a HTML node that will contain the chart ; default is 'document.body'*
  *%open*: false, */// you can set the initial state of the component displaying the content ; default is 'true'*
  *%flattened*: false, */// you can display the CSS classes all flattened, without the parents sections (sets, subsets and properties) ; default is 'false'*
})`

const Type = ({ type, text, code }) => {
  const [open, set_open] = useState(false)
  return (
    <TypeBlock className="dropdown">
      <TypeLabel pb45={!open} onClick={() => set_open(!open)}>
        <Arrow />
        <Span ml30 hover_sapphire3>
          You're a {type}
        </Span>
      </TypeLabel>
      {open && (
        <Div w100p ph20 mb100>
          <Div mv40>{text}</Div>
          <Code>{highlight(code)}</Code>
        </Div>
      )}
    </TypeBlock>
  )
}

const Arrow = (props) => (
  <svg width="50" viewBox="0 0 130 130">
    <path
      fill="none"
      stroke="var(--sapphire3)"
      strokeWidth="4"
      d="M73 18l48 47-48 48M6 65h114"
    />
  </svg>
)

const highlight = (code) => {
  const code_chunks = code.split('*').map((chunk, i) => {
    const color = get_color(chunk)
    if (i % 2) {
      return (
        <Span key={i} {...{ [color]: true }}>
          {chunk.slice(1)}
        </Span>
      )
    }
    return chunk
  })

  return code_chunks
}

const get_color = (chunk) =>
  (chunk.includes('$') && 'sapphire4') ||
  (chunk.includes('%') && 'cerulean5') ||
  (chunk.includes('&') && 'red4') ||
  (chunk.includes('#') && 'blue5') ||
  'grey4'

const Page = Component.pa100.div()
const Title = Component.fs55.sapphire3.mb40.div()
const Code =
  Component.monospace.lh30.fs20.bg_grey1.grey9.pv15.ph20.mt25.ws_pre.w100p.of_scroll.div()
const Section = Component.bb.pb10.sapphire3.b_sapphire2.div()
const Text = Component.mt50.fs30.p()
const TypeBlock = Component.w100p.mb50.ai_start.flex.flex_column.div()
const TypeLabel = Component.mh20.flex.ai_center.grey4.c_pointer.div()

export default App
